import { clsx } from 'clsx';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';

type BadgeColor = 'gray' | 'orange' | 'primary' | 'custom' | 'red';
type BadgeSize = 'sm' | 'md' | 'lg';
type IconPlacement = 'leading' | 'trailing';

const COLOR_CLASS_MAP: Record<BadgeColor, string> = {
  gray: 'bg-gray-d-50 text-gray-d-700 border-gray-d-200',
  orange: 'bg-orange-d-50 text-orange-d-700 border-orange-d-200',
  primary: 'bg-primary-d-50 text-primary-d-700 border-primary-d-200',
  custom: 'text-gray-d-700 border-gray-d-200',
  red: 'bg-error-d-25 text-error-d-700 border-error-d-300',
};

const SIZE_MAP: Record<BadgeSize, string> = {
  sm: 'py-0.5 px-1.5 text-xs',
  md: 'py-0.5 px-2.5 text-sm',
  lg: 'py-1 px-3 text-sm',
};

interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  children: string;
  color: BadgeColor;
  customBackgroundColor?: string;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconPlacement?: IconPlacement;
  size: BadgeSize;
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      children,
      color,
      customBackgroundColor,
      fullWidth = false,
      icon = undefined,
      iconPlacement = undefined,
      size,
      style,
      ...rest
    },
    ref,
  ) => {
    const iconToRender = icon ? (
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {icon}
      </div>
    ) : null;

    return (
      <div
        ref={ref}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-md border',
          COLOR_CLASS_MAP[color],
          SIZE_MAP[size],
          fullWidth ? 'w-full' : '',
        )}
        style={{
          ...style,
          backgroundColor: customBackgroundColor || style?.backgroundColor,
        }}
        {...rest}
      >
        {iconPlacement === 'leading' && iconToRender}
        {children}
        {iconPlacement === 'trailing' && iconToRender}
      </div>
    );
  },
);

Badge.displayName = 'Badge';

export default Badge;
